/**
 * Log Viewer - Status Bar
 *
 * Shows:
 * - Job label (or process UID / channel if no label)
 * - Process status badge (Process Running/Process Stopped) - green for running, red for stopped
 * - LIVE badge (when caught up and tailing) OR Worker state (Idle/Streaming/Paused/Done/Error)
 * - Gaps: number of missing sequences (if any detected)
 * - Size: total stream size across all channels (stderr + stdout)
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { WorkerState, StreamViewStatusUpdate, ArchiveProgress } from "./types";
import { formatBytesCompact } from "../../utils/formatBytes";
import { formatRunDate, formatRunStatus } from "./run-format";
import { ROUTES } from "../../routes/paths";
import {
  fetchProcessRunByUid,
  fetchRecentProcessRuns,
  ProcessRunRow,
} from "./processrun-queries";

export interface StatusBarProps {
  status: StreamViewStatusUpdate;
  processUid: string;
  channel: string;
  /**
   * The run's log channels (from the continuum.stream ledger, resolved by the
   * page). The channel chooser only renders when there is more than one — a
   * single-channel run has nothing to choose between.
   */
  channels?: string[];
  /**
   * The run had a mesh mailbox. Its rpc-inbox/rpc-sent are NOT log channels (they
   * carry RPC envelopes, not logs — see channels.ts), so they are deliberately not in
   * `channels`. But they ARE another way to look at this run, so the chooser offers
   * "Mesh traffic" as a sibling of the log channels, linking to the devtools view that
   * correlates them into request/response pairs.
   *
   * Addressed by PROCESS RUN (?processUid=), never by mailbox key: the server
   * authorizes the run and resolves the mailbox, so a mailbox credential never has to
   * exist in the browser or sit in a URL.
   */
  hasMeshTraffic?: boolean;
  totalCount?: number;
  filteredCount?: number;
  archiveProgress?: ArchiveProgress | null;
}

// The run shape used by the status bar IS the query row shape — single source of
// truth (includes processCompleteKind, needed for the Awol run status).
type ProcessRun = ProcessRunRow;

export function StatusBar(props: StatusBarProps) {
  const {
    status,
    processUid,
    channel,
    channels,
    hasMeshTraffic,
    totalCount,
    filteredCount,
    archiveProgress,
  } = props;
  const [showProcessRunSelector, setShowProcessRunSelector] = useState(false);
  const [showChannelSelector, setShowChannelSelector] = useState(false);
  const [processRuns, setProcessRuns] = useState<ProcessRun[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const popupRef = React.useRef<HTMLDivElement>(null);

  const formatNumber = (n: number) => n.toLocaleString();
  const formatTimestamp = (ms: number) => new Date(ms).toLocaleString();

  const workerStateLabel = getWorkerStateLabel(status.workerState);
  const workerStateClass = getWorkerStateClass(status.workerState);

  const summary = status.summary;
  const isTailing = status.isLive;

  // Label for the status bar — schedule name if available, else job label
  const [displayLabel, setDisplayLabel] = useState<string | null>(null);
  // The current run's row, fetched ONCE per processUid. Both readers of it — the
  // status-bar label and the run picker's job subject — read this state instead
  // of issuing their own lookup; the picker used to re-fetch the same row on
  // every popup open, which cost a whole mesh RPC round trip before the popup
  // could even start loading its list.
  const [currentRun, setCurrentRun] = useState<ProcessRun | null>(null);

  // Fetch the current processrun on mount
  useEffect(() => {
    let cancelled = false;

    async function fetchCurrentRun() {
      try {
        const run = await fetchProcessRunByUid(processUid);
        if (cancelled || !run) return;
        setCurrentRun(run);
        const name = run["event.schedule.name"] || run["job.label"] || null;
        const startedAt = run.startedAt ? formatDate(run.startedAt) : null;
        setDisplayLabel(name && startedAt ? `${name} — ${startedAt}` : name);
      } catch (err) {
        console.error("Failed to fetch process run label:", err);
      }
    }

    fetchCurrentRun();
    return () => {
      cancelled = true;
    };
  }, [processUid]);

  // Fetch the job's other runs when the popup is opened. The subject is the JOB —
  // continuum groups runs by job, and job_uid is the predicate that keeps the
  // query on an index (see fetchRecentProcessRuns for the measurement). The list
  // is re-read on each open rather than cached: a live job gains runs, and a
  // stale picker is worse than one round trip.
  useEffect(() => {
    async function fetchProcessRuns() {
      if (!showProcessRunSelector) return;

      // ProcessRunRow.uid IS the process-run uid, so this also rejects a row
      // left over from a previous processUid — the picker must never list
      // another job's runs while the new row is in flight.
      const run = currentRun && currentRun.uid === processUid ? currentRun : null;
      if (!run) {
        // Hold the popup in its loading state; this effect re-runs when the
        // row for THIS processUid lands.
        setLoading(true);
        return;
      }

      const jobUid = run.job_uid;
      if (!jobUid) {
        setProcessRuns([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const sortedRuns = await fetchRecentProcessRuns({ jobUid });
        setProcessRuns(sortedRuns);
        setLoading(false);
      } catch (err) {
        console.error("Failed to fetch process runs:", err);
        setLoading(false);
      }
    }

    fetchProcessRuns();
  }, [showProcessRunSelector, currentRun, processUid]);

  // formatDate / formatStatus are the pure helpers in run-format.ts (extracted
  // for unit testing; they encode the tz-naive-timestamp and soft-delete-status
  // fixes). Thin local aliases keep the existing call sites unchanged.
  const formatDate = formatRunDate;
  const formatStatus = formatRunStatus;

  const handleProcessRunClick = (selectedProcessUid: string) => {
    setShowProcessRunSelector(false);
    // Deliberately NOT carrying the current channel over: another run of the same
    // job need not have written this channel, and pinning it produced an empty
    // view. Let the page re-resolve against the run we're actually navigating to.
    const url = ROUTES.APP.LOGS_WITH_PARAMS(selectedProcessUid);
    navigate(url);
  };

  const handleChannelClick = (selectedChannel: string) => {
    setShowChannelSelector(false);
    if (selectedChannel === channel) return;
    // Channel changes the record format, so drop any pinned ?parser= and let the
    // page re-derive it (applog -> applog, console -> structured).
    navigate(ROUTES.APP.LOGS_WITH_PARAMS(processUid, selectedChannel));
  };

  // Close popup when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        setShowProcessRunSelector(false);
        setShowChannelSelector(false);
      }
    }

    if (showProcessRunSelector || showChannelSelector) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showProcessRunSelector, showChannelSelector]);

  const processedBytes = summary.bytesProcessed;
  const blocksReceived = summary.blocksProcessed;
  const totalBlocks = summary.totalBlocks;
  const totalBytesValue =
    summary.totalBytes > 0 ? summary.totalBytes : undefined;

  return (
    <div className="status-bar">
      <div className="status-bar__left">
        {/* Process Status Badge (Process Running/Process Stopped) */}
        {status.streamInfo?.startedAt && (
          <span
            className={`status-bar__badge ${
              status.streamInfo.completedAt
                ? "status-bar__badge--ended"
                : "status-bar__badge--running"
            }`}
            title={`Started: ${formatTimestamp(status.streamInfo.startedAt)}${
              status.streamInfo.completedAt
                ? `\nCompleted: ${formatTimestamp(status.streamInfo.completedAt)}`
                : ""
            }`}
          >
            {status.streamInfo.completedAt
              ? "Process Stopped"
              : "Process Running"}
          </span>
        )}

        {/* LIVE Badge or Worker State */}
        {isTailing ? (
          <span className="status-bar__badge status-bar__badge--live">
            LIVE
          </span>
        ) : (
          <span
            className={`status-bar__badge status-bar__badge--${workerStateClass}`}
          >
            {workerStateLabel}
          </span>
        )}

        {/* Archive Progress */}
        {archiveProgress &&
          archiveProgress.phase !== "done" &&
          archiveProgress.archiveCount > 0 && (
            <span className="status-bar__stat" title="Loading from archives">
              <span className="status-bar__stat-label">Archive:</span>
              <span className="status-bar__stat-value">
                {archiveProgress.phase === "listing"
                  ? "Listing..."
                  : archiveProgress.phase === "downloading"
                    ? `Downloading ${archiveProgress.archiveIndex + 1}/${archiveProgress.archiveCount} (${formatBytesCompact(archiveProgress.bytesDownloaded)} / ${formatBytesCompact(archiveProgress.bytesTotal)})`
                    : archiveProgress.phase === "decompressing"
                      ? `Decompressing ${archiveProgress.archiveIndex + 1}/${archiveProgress.archiveCount}`
                      : `Parsing ${archiveProgress.archiveIndex + 1}/${archiveProgress.archiveCount} (${formatNumber(archiveProgress.messagesEmitted)} msgs)`}
              </span>
            </span>
          )}
      </div>

      {/* Job Label - Centered */}
      <div className="status-bar__center" ref={popupRef}>
        <span className="status-bar__service-name">
          {displayLabel ||
            status.streamInfo?.serviceLabel ||
            `${processUid} / ${channel}`}
        </span>
        <button
          className="status-bar__selector-button"
          onClick={() => setShowProcessRunSelector(!showProcessRunSelector)}
          title="View other process runs"
        >
          ▼
        </button>
        {showProcessRunSelector && (
          <div className="status-bar__selector-popup">
            {loading ? (
              <div className="status-bar__selector-loading">Loading...</div>
            ) : processRuns.length === 0 ? (
              <div className="status-bar__selector-empty">
                No process runs found
              </div>
            ) : (
              <div className="status-bar__selector-list">
                {processRuns.map((run) => (
                  <div
                    key={run.uid}
                    className={`status-bar__selector-item ${run.uid === processUid ? "status-bar__selector-item--active" : ""}`}
                    onClick={() => handleProcessRunClick(run.uid)}
                  >
                    <div className="status-bar__selector-item-date">
                      {formatDate(run.startedAt)}
                    </div>
                    <div className="status-bar__selector-item-status">
                      {formatStatus(run)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* View chooser — the run's log channels, plus its mesh traffic when it had
            a mailbox. Renders whenever there is more than ONE thing to look at;
            with a single view there is nothing to choose. Sits beside the
            process-run chooser so "which run / what am I looking at" read together.

            Mesh traffic is not a log channel (rpc-inbox/rpc-sent carry RPC
            envelopes, not logs) — it is a different VIEW of the same run, so it
            navigates to the devtools RPC view rather than switching the log
            channel. */}
        <span className="status-bar__channel-name" title="Log channel">
          {channel}
        </span>
        {(() => {
          const logChannels = channels ?? [];
          const viewCount = logChannels.length + (hasMeshTraffic ? 1 : 0);
          if (viewCount <= 1) return null;
          return (
            <>
              <button
                className="status-bar__selector-button"
                onClick={() => setShowChannelSelector(!showChannelSelector)}
                title="Switch view (log channel / mesh traffic)"
              >
                ▼
              </button>
              {showChannelSelector && (
                <div className="status-bar__selector-popup">
                  <div className="status-bar__selector-list">
                    {logChannels.map((c) => (
                      <div
                        key={c}
                        className={`status-bar__selector-item ${c === channel ? "status-bar__selector-item--active" : ""}`}
                        onClick={() => handleChannelClick(c)}
                      >
                        <div className="status-bar__selector-item-date">{c}</div>
                      </div>
                    ))}
                    {hasMeshTraffic && (
                      <div
                        className="status-bar__selector-item"
                        onClick={() => {
                          setShowChannelSelector(false);
                          navigate(ROUTES.APP.DEVTOOLS_FOR_PROCESS(processUid));
                        }}
                        title="The run's correlated RPC traffic (rpc-inbox + rpc-sent)"
                      >
                        <div className="status-bar__selector-item-date">
                          Mesh traffic
                        </div>
                        <div className="status-bar__selector-item-status">RPC</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          );
        })()}
      </div>

      <div className="status-bar__right">
        {/* Line Count */}
        {totalCount !== undefined && filteredCount !== undefined && (
          <span className="status-bar__stat">
            <span className="status-bar__stat-label">Lines:</span>
            <span className="status-bar__stat-value">
              {filteredCount !== totalCount
                ? `${formatNumber(filteredCount)} / ${formatNumber(totalCount)}`
                : formatNumber(totalCount)}
            </span>
          </span>
        )}

        {/* Blocks - show "Blocks xx / yy" when not tailing, "Blocks yy" when tailing */}
        {blocksReceived > 0 && (
          <span className="status-bar__stat">
            <span className="status-bar__stat-label">Blocks:</span>
            <span className="status-bar__stat-value">
              {isTailing
                ? formatNumber(blocksReceived)
                : totalBlocks > 0 && totalBlocks > blocksReceived
                  ? `${formatNumber(blocksReceived)} / ${formatNumber(totalBlocks)}`
                  : formatNumber(blocksReceived)}
            </span>
          </span>
        )}

        {/* Size - show "Size xx / yy" when not tailing, "Size yy" when tailing */}
        {processedBytes > 0 && (
          <span className="status-bar__stat">
            <span className="status-bar__stat-label">Size:</span>
            <span className="status-bar__stat-value">
              {isTailing
                ? formatBytesCompact(processedBytes)
                : totalBytesValue && totalBytesValue > processedBytes
                  ? `${formatBytesCompact(processedBytes)} / ${formatBytesCompact(totalBytesValue)}`
                  : formatBytesCompact(processedBytes)}
            </span>
          </span>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function getWorkerStateLabel(state: WorkerState): string {
  switch (state) {
    case "idle":
      return "Idle";
    case "consuming":
      return "Streaming";
    case "paused":
      return "Paused";
    case "done":
      return "Done";
    case "error":
      return "Error";
  }
}

function getWorkerStateClass(state: WorkerState): string {
  switch (state) {
    case "idle":
      return "idle";
    case "consuming":
      return "active";
    case "paused":
      return "paused";
    case "done":
      return "done";
    case "error":
      return "error";
  }
}
